import { AxiosError } from 'axios';
import i18n from 'i18n';
import { toast } from 'react-toastify';
import { z } from 'zod';

const ERROR_MESSAGE_FALLBACK = 'Something went wrong!';
const ERROR_ID_LABEL_FALLBACK = 'Error ID';
const LOGOUT_ON_401 = false;

const defaultErrorMessage = i18n.t(
  'errors.genericErrorMessage',
  ERROR_MESSAGE_FALLBACK,
);

const getMessageWithErrorId = (message: string, id: string) => {
  // handle empty message
  const finalMessage = message || defaultErrorMessage;
  const errorIdLabel = i18n.t('errors.errorId', ERROR_ID_LABEL_FALLBACK);
  return `${finalMessage}\n${errorIdLabel}: ${id}`;
};

// ==================

interface IError {
  message: string | string[];
  errorId?: string;
}

const isError = (error: unknown): error is Error => {
  return error instanceof Error;
};

export const isAxiosError = (error: unknown): error is AxiosError<IError> => {
  if (!isError(error)) return false;

  return (error as AxiosError).isAxiosError;
};

export const isUnauthorized = (error: unknown): boolean => {
  const UNAUTHORIZED_STATUS_CODES = [401];

  return (
    isAxiosError(error) &&
    UNAUTHORIZED_STATUS_CODES.includes(Number(error.response?.status))
  );
};

const getError = (error: unknown): { message: string; code: string } => {
  if (isAxiosError(error)) {
    if (error.response?.data instanceof ArrayBuffer) {
      const decoder = new TextDecoder('utf-8');
      const jsonText = decoder.decode(new Uint8Array(error.response.data));

      const parsed = z
        .object({
          message: z.string().catch(''),
          errorId: z.string().catch(''),
        })
        .parse(JSON.parse(jsonText));

      return {
        message: parsed.message,
        code: parsed.errorId,
      };
    }

    const { message = '', errorId = '' } = error.response?.data ?? {};

    if (Array.isArray(message))
      return {
        message: message[0],
        code: errorId,
      };

    return {
      message,
      code: errorId,
    };
  }

  if (isError(error)) {
    return {
      message: error.message,
      code: error.name,
    };
  }

  return {
    message: '',
    code: '',
  };
};

const getErrorMessage = (error: unknown): { message: string; code: string } => {
  const { message, code } = getError(error);

  if (message)
    return {
      message,
      code,
    };

  return {
    message: defaultErrorMessage,
    code,
  };
};

export const onError = async (error: unknown) => {
  if (LOGOUT_ON_401 && isUnauthorized(error)) {
    const { loaders } = await import('store/auth');
    await loaders.signout();
  }

  throw error;
};

export const handleError = (error: unknown) => {
  const { message, code } = getErrorMessage(error);

  toast.error(getMessageWithErrorId(message, code));
};
